/**
 * Which sounds a shot makes, and when (SND-001).
 *
 * The physics already records every contact with its impact speed (ARCHITECTURE.md
 * §5.6): this turns that log into sound cues. Pure, so it is tested in Node; the
 * synthesis that makes them audible is `synth.ts`.
 *
 * Rule 5 (CLAUDE.md §4): nothing may read the event log ahead of the animation clock.
 * A cursor hands out only the events the playback has just passed, so a ball never
 * clicks before it is seen to touch, and a pocket never drops before the ball gets there.
 */
import type { SimEvent } from '@potted/sim';

export type CueKind = 'strike' | 'ball' | 'cushion' | 'pocket' | 'scratch' | 'cleared' | 'tick';

export interface Cue {
  readonly kind: CueKind;
  /** 0–1, before the player's effects volume is applied. */
  readonly gain: number;
}

/**
 * Impact speeds, m/s, mapped onto loudness. A contact slower than `quietMps` is a
 * touch nobody would hear (balls settling against each other, a creep into a rail);
 * `loudMps` and above is full volume. A square root between them, because loudness
 * reads roughly logarithmically and a linear map makes gentle shots near-silent.
 */
export const LOUDNESS = {
  ball: { quietMps: 0.05, loudMps: 4 },
  cushion: { quietMps: 0.08, loudMps: 4 },
  /** A pot sounds even when the ball trickles in: the drop is gravity, not speed. */
  pocketFloor: 0.45,
  /** The most cues one frame may start: a break of ten balls must not become a roar. */
  maxPerFrame: 4,
} as const;

/** 0–1 loudness for an impact speed, 0 below the quiet threshold. */
export function loudness(speedMps: number, quietMps: number, loudMps: number): number {
  if (!(speedMps > quietMps)) return 0;
  const t = (speedMps - quietMps) / (loudMps - quietMps);
  return t >= 1 ? 1 : Math.sqrt(t);
}

/** The cue ball struck: louder with more power (0–1). */
export function strikeCue(power: number): Cue {
  const p = power > 1 ? 1 : power < 0 ? 0 : power;
  return { kind: 'strike', gain: 0.25 + 0.75 * Math.sqrt(p) };
}

/** One event as a cue, or undefined for events that make no sound. */
export function eventCue(event: SimEvent): Cue | undefined {
  switch (event.type) {
    case 'ball-ball': {
      const gain = loudness(event.speedMps, LOUDNESS.ball.quietMps, LOUDNESS.ball.loudMps);
      return gain > 0 ? { kind: 'ball', gain } : undefined;
    }
    case 'cushion': {
      const gain = loudness(event.speedMps, LOUDNESS.cushion.quietMps, LOUDNESS.cushion.loudMps);
      return gain > 0 ? { kind: 'cushion', gain } : undefined;
    }
    case 'pocket':
    case 'scratch': {
      const speed = loudness(event.speedMps, 0, LOUDNESS.ball.loudMps);
      const gain = LOUDNESS.pocketFloor + (1 - LOUDNESS.pocketFloor) * speed;
      return { kind: event.type === 'scratch' ? 'scratch' : 'pocket', gain };
    }
    default:
      // 'roll', 'rest' and 'cap' are bookkeeping, not sounds.
      return undefined;
  }
}

export interface CueCursor {
  /**
   * The cues for every event at or before `tS` not handed out already, loudest first
   * and at most `LOUDNESS.maxPerFrame`. Call it once per animation frame with the
   * playback time; it never looks past `tS`.
   */
  advance(tS: number): Cue[];
}

export function createCueCursor(events: readonly SimEvent[]): CueCursor {
  let next = 0;
  return {
    advance(tS) {
      const cues: Cue[] = [];
      while (next < events.length) {
        const event = events[next];
        if (event === undefined || event.tS > tS) break;
        next++;
        const cue = eventCue(event);
        if (cue !== undefined) cues.push(cue);
      }
      if (cues.length <= LOUDNESS.maxPerFrame) return cues;
      // A pot always sounds; then the loudest contacts.
      return cues.sort((a, b) => rank(b) - rank(a)).slice(0, LOUDNESS.maxPerFrame);
    },
  };
}

function rank(cue: Cue): number {
  return (cue.kind === 'pocket' || cue.kind === 'scratch' ? 10 : 0) + cue.gain;
}
